import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardCalendarWidget = ({ embedded = false }) => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  const fetchUpcomingEvents = async () => {
    try {
      const response = await axios.get(`${API}/calendar/upcoming`, {
        params: { days: 7 }
      });
      setUpcomingEvents(response.data);
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatEventTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((date - now) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    return date.toLocaleDateString();
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-500',
      high: 'bg-orange-500',
      medium: 'bg-blue-500',
      low: 'bg-gray-500'
    };
    return colors[priority] || colors.medium;
  };

  if (loading) {
    if (embedded) {
      return <div className="h-full flex items-center justify-center text-muted-foreground">Loading events...</div>;
    }
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Events
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">Loading events...</div>
        </CardContent>
      </Card>
    );
  }

  // Embedded mode for dashboard builder
  if (embedded) {
    return (
      <div className="h-full flex flex-col p-3">
        {upcomingEvents.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <Calendar className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No upcoming events</p>
          </div>
        ) : (
          <div className="space-y-2 overflow-auto">
            {upcomingEvents.slice(0, 5).map((event) => (
              <div
                key={event.id}
                onClick={() => navigate('/calendar')}
                className="p-2 rounded-lg border hover:bg-accent transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <div className={`w-1 h-8 rounded-full ${getPriorityColor(event.priority)}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{event.title}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatEventTime(event.start_date)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Events
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/calendar')}>
            View All
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {upcomingEvents.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No upcoming events</p>
            <Button variant="link" onClick={() => navigate('/calendar')} className="mt-2">
              Create an event
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingEvents.slice(0, 5).map((event) => (
              <button
                key={event.id}
                onClick={() => navigate('/calendar')}
                className="w-full text-left p-3 rounded-lg border border-border hover:bg-accent transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-1 h-full rounded-full ${getPriorityColor(event.priority)}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-foreground truncate group-hover:text-primary transition-colors">
                        {event.title}
                      </div>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {event.event_type}
                      </Badge>
                    </div>
                    
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatEventTime(event.start_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(event.start_date).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      {event.location && (
                        <span className="flex items-center gap-1 truncate">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span className="truncate">{event.location}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
            
            {upcomingEvents.length > 5 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/calendar')}
                className="w-full"
              >
                +{upcomingEvents.length - 5} more events
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardCalendarWidget;
